import { useState } from "react";
import api from "@/lib/api";
import { VendorStatusBadge } from "@/pages/farm/shared";
import ImageUploader from "@/components/ImageUploader";

export default function ProfileTab({ me, onSaved }) {
  const v = me.vendor;
  const [form, setForm] = useState({
    farm_name: v.farm_name || "",
    owner_name: v.owner_name || "",
    location: v.location || "",
    story: v.story || "",
    image_url: v.image_url || "",
    video_url: v.video_url || "",
    logo_url: v.logo_url || "",
    practices: v.practices || [],
    certifications: (v.certifications || []).join(", "),
    specialties: (v.specialties || []).join(", "),
    animals_raised: (v.animals_raised || []).join(", "),
    shipping_info: v.shipping_info || "",
    ships_to_regions: v.ships_to_regions || "",
    website_url: v.website_url || "",
  });
  const PRACTICES = ["Grass-fed","Grass-finished","Pasture-raised","Grain-finished","No antibiotics","No added hormones","Regenerative","Dry-aged","Wild-caught","Heritage breed","Certified organic"];
  const splitList = (s) => s.split(",").map(x => x.trim()).filter(Boolean);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const save = async (submit = false) => {
    setBusy(true); setError(""); setSuccess("");
    try {
      const payload = { ...form, certifications: splitList(form.certifications), specialties: splitList(form.specialties), animals_raised: splitList(form.animals_raised), submit_for_review: submit };
      const { data } = await api.put("/farm/profile", payload);
      setSuccess(submit ? "Profile submitted for review — we'll be in touch within 1 business day." : "Profile saved.");
      onSaved?.(data);
    } catch (e) {
      setError(e.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const isPending = v.vendor_status === "pending_invite";
  return (
    <div className="space-y-6" data-testid="farm-profile-tab">
      <div className="flex items-center gap-3 flex-wrap">
        <h3 className="font-display text-2xl">Your storefront</h3>
        <VendorStatusBadge status={v.vendor_status}/>
      </div>
      <p className="text-sm text-[var(--ff-text-muted)]">
        This is how members see your farm on the marketplace. {isPending ? "Once your profile is complete, submit it for our team's review." : "Changes save instantly and update your public profile."}
      </p>

      {error && <div className="bg-rose-50 border border-rose-200 p-3 rounded text-sm text-rose-800" data-testid="profile-error">{error}</div>}
      {success && <div className="bg-emerald-50 border border-emerald-200 p-3 rounded text-sm text-emerald-800" data-testid="profile-success">{success}</div>}

      <div className="card-flat p-5 grid md:grid-cols-2 gap-4" data-testid="profile-form">
        <label className="block">
          <span className="overline">Farm name</span>
          <input className="input-base mt-2" value={form.farm_name} onChange={e => setForm({...form, farm_name: e.target.value})} data-testid="profile-farm-name"/>
        </label>
        <label className="block">
          <span className="overline">Owner name</span>
          <input className="input-base mt-2" value={form.owner_name} onChange={e => setForm({...form, owner_name: e.target.value})} data-testid="profile-owner-name"/>
        </label>
        <label className="block md:col-span-2">
          <span className="overline">Location</span>
          <input className="input-base mt-2" placeholder="e.g. Vero Beach, FL" value={form.location} onChange={e => setForm({...form, location: e.target.value})} data-testid="profile-location"/>
        </label>
        <label className="block md:col-span-2">
          <span className="overline">Header image</span>
          <div className="mt-2">
            <ImageUploader
              value={form.image_url}
              onChange={(url) => setForm({...form, image_url: url})}
              folder="farms"
              testId="profile-image"
            />
          </div>
        </label>
        <label className="block md:col-span-2">
          <span className="overline">Logo</span>
          <div className="mt-2">
            <ImageUploader value={form.logo_url} onChange={(url) => setForm({...form, logo_url: url})} folder="farms" testId="profile-logo"/>
          </div>
        </label>
        <div className="md:col-span-2">
          <span className="overline">Ranching practices</span>
          <div className="mt-2 flex flex-wrap gap-2" data-testid="profile-practices">
            {PRACTICES.map(pr => {
              const on = form.practices.includes(pr);
              return <button type="button" key={pr} onClick={() => setForm({...form, practices: on ? form.practices.filter(x => x !== pr) : [...form.practices, pr]})}
                className={`px-3 py-1 rounded-full text-xs border transition-colors ${on ? "bg-[var(--ff-primary)] text-white border-[var(--ff-primary)]" : "border-[var(--ff-border)] hover:border-[var(--ff-primary)]"}`}>{pr}</button>;
            })}
          </div>
        </div>
        <label className="block">
          <span className="overline">Certifications (comma-separated)</span>
          <input className="input-base mt-2" placeholder="USDA-inspected, Animal Welfare Approved" value={form.certifications} onChange={e => setForm({...form, certifications: e.target.value})} data-testid="profile-certifications"/>
        </label>
        <label className="block">
          <span className="overline">Product specialties (comma-separated)</span>
          <input className="input-base mt-2" placeholder="Dry-aged ribeye, Brisket" value={form.specialties} onChange={e => setForm({...form, specialties: e.target.value})} data-testid="profile-specialties"/>
        </label>
        <label className="block">
          <span className="overline">Animals raised (comma-separated)</span>
          <input className="input-base mt-2" placeholder="Cattle, Chicken" value={form.animals_raised} onChange={e => setForm({...form, animals_raised: e.target.value})} data-testid="profile-animals"/>
        </label>
        <label className="block">
          <span className="overline">Ships to</span>
          <input className="input-base mt-2" placeholder="Lower 48 states" value={form.ships_to_regions} onChange={e => setForm({...form, ships_to_regions: e.target.value})} data-testid="profile-ships-to"/>
        </label>
        <label className="block md:col-span-2">
          <span className="overline">Shipping information (shown to customers)</span>
          <textarea rows={3} className="input-base mt-2" placeholder="Ship days, cold-chain packaging, transit times, free-shipping thresholds…" value={form.shipping_info} onChange={e => setForm({...form, shipping_info: e.target.value})} data-testid="profile-shipping-info"/>
        </label>
        <label className="block md:col-span-2">
          <span className="overline">Website</span>
          <input type="url" className="input-base mt-2" placeholder="https://" value={form.website_url} onChange={e => setForm({...form, website_url: e.target.value})} data-testid="profile-website"/>
        </label>
        <label className="block md:col-span-2">
          <span className="overline">Farm story video (optional)</span>
          <input
            type="url"
            className="input-base mt-2"
            placeholder="Paste a YouTube link or direct .mp4/.webm URL — auto-plays muted on your farm profile"
            value={form.video_url}
            onChange={e => setForm({...form, video_url: e.target.value})}
            data-testid="profile-video-url"
          />
          <p className="text-xs text-[var(--ff-text-muted)] mt-1">
            Short clip (30–90s) of you or your farm plays as the hero on your profile.
          </p>
        </label>
        <label className="block md:col-span-2">
          <span className="overline">Your story</span>
          <textarea rows={8} className="input-base mt-2" placeholder="Tell members about your farm, your family, your growing practices, what makes your products special…"
            value={form.story} onChange={e => setForm({...form, story: e.target.value})} data-testid="profile-story"/>
        </label>
      </div>

      <div className="flex flex-wrap gap-3">
        <button disabled={busy} onClick={() => save(false)} className="btn-secondary" data-testid="profile-save-btn">{busy ? "Saving…" : "Save changes"}</button>
        {isPending && (
          <button disabled={busy || !form.farm_name || !form.location || !form.story} onClick={() => save(true)} className="btn-primary" data-testid="profile-submit-btn">
            Submit for review
          </button>
        )}
      </div>
    </div>
  );
}
